import React from 'react';
import { Stack, Title, Divider, Text, Textarea, Group, Button, Alert } from '@mantine/core';
import { IconCheck, IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { RatingInput } from './RatingInput';
import { QuestionSection, Review360Answer } from '../../types';

interface QuestionnaireFormProps {
  sections: QuestionSection[];
  answers: Review360Answer[];
  raterRole: string;
  isSubmitted: boolean;
  /** Цикл больше не собирает ответы: анкету можно только прочитать. */
  isClosed?: boolean;
  onAnswerChange: (questionId: string, sectionId: string, value: number | string) => void;
  onSave: () => void;
  onSubmit: () => void;
  isSaving: boolean;
  isSubmitting: boolean;
}

export const QuestionnaireForm: React.FC<QuestionnaireFormProps> = ({
  sections,
  answers,
  raterRole,
  isSubmitted,
  isClosed = false,
  onAnswerChange,
  onSave,
  onSubmit,
  isSaving,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const isLocked = isSubmitted || isClosed;

  const getAnswer = (questionId: string): number | string | undefined => {
    const answer = answers.find((a) => a.questionId === questionId);
    return answer?.value;
  };

  return (
    <Stack gap="lg">
      {isSubmitted && (
        <Alert color="teal" icon={<IconCheck size={16} />}>
          {t('review360.responseSubmitted')}
        </Alert>
      )}
      {isClosed && !isSubmitted && (
        <Alert color="gray" icon={<IconLock size={16} />}>
          {t('review360.responseClosed')}
        </Alert>
      )}

      {sections.map((section) => {
        const visibleQuestions = section.questions.filter(
          (q) =>
            !q.applicableRoles ||
            q.applicableRoles.length === 0 ||
            q.applicableRoles.includes(raterRole as never),
        );

        if (visibleQuestions.length === 0) return null;

        return (
          <Stack key={section._id} gap="md">
            <Title order={4}>{section.title}</Title>
            <Divider />

            {visibleQuestions.map((question) => {
              const currentValue = getAnswer(question._id);

              return (
                <Stack key={question._id} gap="xs">
                  <Text fw={500}>
                    {question.text}
                    {question.required && (
                      <Text component="span" c="red" ml={4}>
                        *
                      </Text>
                    )}
                  </Text>

                  {question.type === 'rating' ? (
                    <RatingInput
                      value={typeof currentValue === 'number' ? currentValue : null}
                      onChange={(v) => onAnswerChange(question._id, section._id, v)}
                      disabled={isLocked}
                    />
                  ) : (
                    <Textarea
                      value={typeof currentValue === 'string' ? currentValue : ''}
                      onChange={(e) =>
                        onAnswerChange(question._id, section._id, e.currentTarget.value)
                      }
                      autosize
                      minRows={3}
                      disabled={isLocked}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        );
      })}

      {!isLocked && (
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onSave} loading={isSaving}>
            {t('review360.saveDraft')}
          </Button>
          <Button color="teal" onClick={onSubmit} loading={isSubmitting}>
            {t('review360.submitResponse')}
          </Button>
        </Group>
      )}
    </Stack>
  );
};
